import React, { useEffect, useState, useCallback, useMemo, useRef } from 'react';
import AdminNav from '../../components/admin/AdminNav';
import { toast } from '../../utils/toast';
import 'react-toastify/dist/ReactToastify.css';
import api from '../../utils/api';
import { FaSearch, FaEdit, FaTrash, FaInfoCircle } from 'react-icons/fa';
import ConfirmationModal from '../../components/common/ConfirmationModal';
import SupplierInfoModal from '../../components/common/SupplierInfoModal';
import { NAME_LIMITS } from '../../utils/nameLimits';
import { getCurrentUser } from '../../utils/auth';
import { useTranslation } from 'react-i18next';

import SupplierImages from '../../components/common/SupplierImages';
import { US_STATES, DEFAULT_STATE, stateLabel } from '../../utils/usStates';
import TruncatedText from '../../components/common/TruncatedText';
import Pagination from '../../components/common/Pagination';
import ResultCount from '../../components/common/ResultCount';
import { useIsMobile } from '../../utils/useIsMobile';

// Create and delete are super-admin only; supplier admins see only their linked suppliers (server enforces both)

const SUPPLIERS_PER_PAGE = 5;

// Underlying value per sortable column, so sorting ignores formatted display text
const SORT_VALUES = {
  name: (s) => (s.name || '').toLowerCase(),
  email: (s) => (s.email || '').toLowerCase(),
  phone: (s) => s.phone || '',
  address: (s) => [s.address?.street, s.address?.city, s.address?.state, s.address?.zip].filter(Boolean).join(' ').toLowerCase(),
};

export default function AdminSupplierPage() {
  const { t } = useTranslation();
  // Read by the load effect, which must not re-run when the language changes
  const tRef = useRef(t);
  tRef.current = t;
  // Read on each render so a login in this session takes effect without reloading the app
  const isSuperAdminUser = getCurrentUser()?.role === 'superadmin';
  const [suppliers, setSuppliers] = useState([]);
  const [filteredSuppliers, setFilteredSuppliers] = useState([]);
  const [searchQuery, setSearchQuery] = useState('');
  const [name, setName] = useState('');
  const [displayName, setDisplayName] = useState('');
  const [email, setEmail] = useState('');
  const [phone, setPhone] = useState('');
  const [address, setAddress] = useState({ street: '', city: '', state: '', zip: '' });
  // Delivery: one flat fee for every ZIP code in the allowed states
  const [offerDelivery, setOfferDelivery] = useState(true);
  const [flatFee, setFlatFee] = useState('');
  const [estimatedDays, setEstimatedDays] = useState('');
  const [allowedStates, setAllowedStates] = useState([DEFAULT_STATE]);
  // Cutting & packaging: a fee kept separate from delivery
  const [cuttingPackagingFee, setCuttingPackagingFee] = useState('');
  const [editingSupplier, setEditingSupplier] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [sortConfig, setSortConfig] = useState({ key: null, direction: 'asc' });
  const [loading, setLoading] = useState(false);
  const [noLinkedSupplier, setNoLinkedSupplier] = useState(false);

  const [showConfirm, setShowConfirm] = useState(false);
  const [supplierToDelete, setSupplierToDelete] = useState(null);

  const [selectedSupplier, setSelectedSupplier] = useState(null);
  const [modalOpen, setModalOpen] = useState(false);
  const isMobile = useIsMobile(); // below md the list becomes expandable cards
  const [expandedCardId, setExpandedCardId] = useState(null);

  const indexOfLastSupplier = currentPage * SUPPLIERS_PER_PAGE;
  const indexOfFirstSupplier = indexOfLastSupplier - SUPPLIERS_PER_PAGE;
  const sortedSuppliers = useMemo(() => {
    if (!sortConfig.key) return filteredSuppliers;
    const dir = sortConfig.direction === 'asc' ? 1 : -1;
    const valueOf = SORT_VALUES[sortConfig.key];
    return [...filteredSuppliers].sort((a, b) => {
      const av = valueOf(a);
      const bv = valueOf(b);
      if (av < bv) return -dir;
      if (av > bv) return dir;
      return 0;
    });
  }, [filteredSuppliers, sortConfig]);
  const currentSuppliers = sortedSuppliers.slice(indexOfFirstSupplier, indexOfLastSupplier);
  const totalPages = Math.ceil(sortedSuppliers.length / SUPPLIERS_PER_PAGE);

  const sortByKey = (key) => {
    const direction = sortConfig.key === key && sortConfig.direction === 'asc' ? 'desc' : 'asc';
    setSortConfig({ key, direction });
  };
  const sortArrow = (key) => <span className="inline-block w-3 text-center">{sortConfig.key !== key ? '' : sortConfig.direction === 'asc' ? '↑' : '↓'}</span>;

  const fetchSuppliers = useCallback(async () => {
    setLoading(true);
    setNoLinkedSupplier(false);
    try {
      const res = await api.get('/api/suppliers/my-suppliers');
      const list = Array.isArray(res.data) ? res.data : [];
      setSuppliers(list);
      setFilteredSuppliers(list);
      setCurrentPage(1);
      if (list.length === 0) setNoLinkedSupplier(true);
    } catch (err) {
      console.error('Failed to fetch suppliers:', err);
      if (err.response) {
        if (
          err.response.status === 404 &&
          err.response.data?.message === 'Associated supplier not found.'
        ) {
          toast.error(tRef.current('adminSuppliers.notLinkedToast'));
          setSuppliers([]);
          setFilteredSuppliers([]);
          setNoLinkedSupplier(true);
        } else if (err.response.status === 403) {
          toast.error(tRef.current('adminSuppliers.forbidden'));
        } else {
          toast.error(tRef.current('adminSuppliers.loadFailed'));
        }
      } else {
        toast.error(tRef.current('adminSuppliers.networkError'));
      }
    } finally {
      setLoading(false);
    }
  }, []);

  useEffect(() => {
    fetchSuppliers();
  }, [fetchSuppliers]);

  const handleSearch = useCallback(
    (query) => {
      setSearchQuery(query);
      const lower = query.toLowerCase();
      setFilteredSuppliers(
        suppliers.filter((s) => s.name?.toLowerCase().includes(lower))
      );
      setCurrentPage(1);
    },
    [suppliers]
  );

  useEffect(() => {
    if (searchQuery === '') setFilteredSuppliers(suppliers);
  }, [suppliers, searchQuery]);

  const handleAddressChange = (field, value) => {
    setAddress((prev) => ({ ...prev, [field]: value }));
  };

  const resetForm = () => {
    setName('');
    setDisplayName('');
    setEmail('');
    setPhone('');
    setAddress({ street: '', city: '', state: '', zip: '' });
    setOfferDelivery(true);
    setFlatFee('');
    setEstimatedDays('');
    setAllowedStates([DEFAULT_STATE]);
    setCuttingPackagingFee('');
    setEditingSupplier(null);
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    try {
      const payload = {
        name,
        displayName,
        email,
        phone,
        address,
        offerDelivery,
        flatFee: flatFee === '' ? 0 : flatFee,
        estimatedDays: estimatedDays === '' ? 0 : estimatedDays,
        allowedStates,
        cuttingPackagingFee: cuttingPackagingFee === '' ? 0 : cuttingPackagingFee,
      };
      if (editingSupplier) {
        const res = await api.put(`/api/suppliers/${editingSupplier._id}`, payload);
        const updated = res.data;
        toast.success(`Supplier "${updated.name}" updated!`);
        setSuppliers((prev) => prev.map((s) => (s._id === updated._id ? updated : s)));
        setFilteredSuppliers((prev) =>
          prev.map((s) => (s._id === updated._id ? updated : s))
        );
      } else {
        const res = await api.post('/api/suppliers', payload);
        const created = res.data;
        toast.success(`Supplier "${created.name}" added!`);
        setSuppliers((prev) => [created, ...prev]);
        setFilteredSuppliers((prev) => [created, ...prev]);
      }
      resetForm();
      fetchSuppliers();
    } catch (err) {
      console.error('Save supplier failed:', err);
      toast.error(err.response?.data?.message || t('adminSuppliers.saveFailed'));
    }
  };

  const handleEdit = (supplier) => {
    setEditingSupplier(supplier);
    setName(supplier.name);
    setDisplayName(supplier.displayName || '');
    setEmail(supplier.email || '');
    setPhone(supplier.phone || '');
    setAddress(supplier.address || { street: '', city: '', state: '', zip: '' });
    setOfferDelivery(supplier.shippingMethods?.delivery !== false);
    setFlatFee(String(supplier.delivery?.flatFee ?? 0));
    setEstimatedDays(String(supplier.delivery?.estimatedDays ?? 3));
    setAllowedStates(supplier.delivery?.allowedStates?.length ? supplier.delivery.allowedStates : [DEFAULT_STATE]);
    setCuttingPackagingFee(String(supplier.cuttingPackagingFee ?? 0));
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  const handleDeleteClick = (supplier) => {
    setSupplierToDelete(supplier);
    setShowConfirm(true);
  };

  const confirmDelete = async () => {
    if (!supplierToDelete) return;
    try {
      await api.delete(`/api/suppliers/${supplierToDelete._id}`);
      setSuppliers((prev) => prev.filter((s) => s._id !== supplierToDelete._id));
      setFilteredSuppliers((prev) => prev.filter((s) => s._id !== supplierToDelete._id));
      toast.success(`Supplier "${supplierToDelete.name}" deleted.`);
    } catch (err) {
      console.error('Delete failed:', err);
      toast.error(err.response?.data?.message || t('adminSuppliers.deleteFailed'));
    } finally {
      setShowConfirm(false);
      setSupplierToDelete(null);
    }
  };

  const cancelDelete = () => {
    setShowConfirm(false);
    setSupplierToDelete(null);
  };

  const openInfoModal = (supplier) => {
    setSelectedSupplier(supplier);
    setModalOpen(true);
  };

  // Photo controls shared by the desktop cell and the mobile card
  const renderPhotos = (supplier) => (
    <SupplierImages
      supplierId={supplier._id}
      images={supplier.images}
      canManage
      compact
      maxGallery={1}
      onChange={(images) => setSuppliers((prev) => prev.map((s) => (s._id === supplier._id ? { ...s, images } : s)))}
    />
  );

  // Mobile card: name and email always visible; the rest of the desktop row and its actions sit under the expand control
  const renderSupplierCard = (supplier) => {
    const isOpen = expandedCardId === supplier._id;
    const address = [supplier.address?.street, supplier.address?.city, stateLabel(supplier.address?.state), supplier.address?.zip].filter(Boolean).join(', ');
    const btnBase = 'inline-flex items-center gap-1 px-3 py-1 rounded text-white whitespace-nowrap text-sm';
    return (
      <li key={supplier._id} className="min-w-0 border rounded-md bg-white shadow-sm p-3 text-sm">
        <div className="flex items-start gap-2 min-w-0">
          <div className="min-w-0 flex-1">
            <div className="font-medium text-gray-800 truncate" title={supplier.displayName || supplier.name}>{supplier.displayName || supplier.name}</div>
            <div className="text-gray-600 truncate" title={supplier.email || ''}>{supplier.email || '-'}</div>
          </div>
          <button type="button" onClick={() => setExpandedCardId(isOpen ? null : supplier._id)} aria-expanded={isOpen} aria-label={t('adminUsers.toggleDetails')} className="shrink-0 p-1 text-blue-600 focus:outline-none">
            <i className={`fas ${isOpen ? 'fa-chevron-down' : 'fa-chevron-right'}`} aria-hidden="true" />
          </button>
        </div>
        {isOpen && (
          <div className="mt-3 border-t border-gray-200 pt-2 space-y-2 min-w-0">
            <dl className="grid grid-cols-[auto_minmax(0,1fr)] gap-x-2 gap-y-1 text-xs text-gray-600">
              <dt className="font-medium text-gray-700">{t('adminSuppliers.colName')}</dt>
              <dd className="min-w-0 break-words">{supplier.name}</dd>
              <dt className="font-medium text-gray-700">{t('adminSuppliers.phone')}</dt>
              <dd className="min-w-0 break-all">{supplier.phone || '-'}</dd>
              <dt className="font-medium text-gray-700">{t('adminSuppliers.address')}</dt>
              <dd className="min-w-0 break-words">{address || '-'}</dd>
              <dt className="font-medium text-gray-700">{t('adminSuppliers.photos')}</dt>
              <dd className="min-w-0">{renderPhotos(supplier)}</dd>
            </dl>
            <div className="flex flex-wrap gap-2">
              <button onClick={() => openInfoModal(supplier)} className={`bg-indigo-500 ${btnBase}`}>
                <FaInfoCircle /> {t('adminUsers.info')}
              </button>
              <button onClick={() => handleEdit(supplier)} className={`bg-yellow-500 ${btnBase}`}>
                <FaEdit /> {t('adminUsers.edit')}
              </button>
              {isSuperAdminUser && (
                <button onClick={() => handleDeleteClick(supplier)} className={`bg-red-600 ${btnBase}`}>
                  <FaTrash /> {t('adminUsers.delete')}
                </button>
              )}
            </div>
          </div>
        )}
      </li>
    );
  };

  return (
    <div className="min-h-screen bg-gray-100">
      <AdminNav />
      <div className="max-w-7xl mx-auto bg-white shadow p-6 rounded-md">
        <div className="flex justify-between items-center mb-4 flex-wrap gap-4">
          <h1 className="text-2xl font-semibold text-gray-800">{t('adminSuppliers.title')}</h1>
        </div>

        <div className="mb-4 flex flex-col sm:flex-row sm:items-center gap-4">
          <div className="relative flex-grow">
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => handleSearch(e.target.value)}
              placeholder={t('adminSuppliers.search')}
              className="border p-2 w-full pr-10 rounded"
            />
            <div className="absolute right-2 top-1/2 transform -translate-y-1/2 text-gray-500">
              <FaSearch />
            </div>
          </div>
        </div>

        {noLinkedSupplier && suppliers.length === 0 && (
          <div className="mb-4 p-4 bg-yellow-100 border border-yellow-300 rounded">
            <p className="text-sm text-yellow-800">
              {t('adminSuppliers.notLinked')}
            </p>
          </div>
        )}

        <form onSubmit={handleSubmit} className={`space-y-2 mb-6 ${isSuperAdminUser || editingSupplier ? '' : 'hidden'}`}>
          <h3 className="text-lg font-semibold">
            {editingSupplier ? t('adminSuppliers.editSupplier') : t('adminSuppliers.addNew')}
          </h3>
          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium mb-1">
                {t('adminSuppliers.name')}{!isSuperAdminUser && <span className="text-gray-500 font-normal"> {t('adminSuppliers.superAdminOnly')}</span>}
              </label>
              <input
                type="text"
                value={name}
                disabled={!isSuperAdminUser}
                onChange={(e) => setName(e.target.value)}
                maxLength={NAME_LIMITS.supplierName.max}
                placeholder={t('adminSuppliers.name')}
                className={`border p-2 w-full rounded ${isSuperAdminUser ? '' : 'bg-gray-100 text-gray-500 cursor-not-allowed'}`}
                required
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminSuppliers.displayName')}</label>
              <input
                type="text"
                value={displayName}
                onChange={(e) => setDisplayName(e.target.value)}
                maxLength={NAME_LIMITS.supplierDisplayName.max}
                placeholder={t('adminSuppliers.displayName')}
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminUsers.email')}</label>
              <input
                type="email"
                value={email}
                onChange={(e) => setEmail(e.target.value)}
                placeholder={t('adminUsers.email')}
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminSuppliers.phone')}</label>
              <input
                type="tel"
                value={phone}
                onChange={(e) => setPhone(e.target.value)}
                placeholder={t('adminSuppliers.phone')}
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminSuppliers.street')}</label>
              <input
                type="text"
                value={address.street || ''}
                onChange={(e) => handleAddressChange('street', e.target.value)}
                placeholder={t('adminSuppliers.street')}
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminSuppliers.city')}</label>
              <input
                type="text"
                value={address.city || ''}
                onChange={(e) => handleAddressChange('city', e.target.value)}
                placeholder={t('adminSuppliers.city')}
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('createGroup.state')}</label>
              <input
                type="text"
                value={address.state || ''}
                onChange={(e) => handleAddressChange('state', e.target.value)}
                placeholder={t('createGroup.state')}
                className="border p-2 w-full rounded"
              />
            </div>
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminSuppliers.zip')}</label>
              <input
                type="text"
                value={address.zip || ''}
                onChange={(e) => handleAddressChange('zip', e.target.value)}
                placeholder={t('adminSuppliers.zip')}
                className="border p-2 w-full rounded"
              />
            </div>
          </div>

          <fieldset className="border rounded p-3 mt-2">
            <legend className="px-1 text-sm font-medium">{t('adminSuppliers.deliveryLegend')}</legend>
            <label className="flex items-center gap-2 text-sm mb-2">
              <input
                type="checkbox"
                checked={offerDelivery}
                onChange={(e) => setOfferDelivery(e.target.checked)}
              />
              {t('adminSuppliers.offerDelivery')}
            </label>
            <div className="grid grid-cols-1 sm:grid-cols-3 gap-2">
              <div>
                <label className="block text-sm font-medium mb-1">{t('adminSuppliers.flatFee')}</label>
                <input
                  type="number"
                  min="0"
                  step="0.01"
                  value={flatFee}
                  onChange={(e) => setFlatFee(e.target.value)}
                  disabled={!offerDelivery}
                  className="border p-2 w-full rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('adminSuppliers.estimatedDays')}</label>
                <input
                  type="number"
                  min="0"
                  step="1"
                  value={estimatedDays}
                  onChange={(e) => setEstimatedDays(e.target.value)}
                  disabled={!offerDelivery}
                  className="border p-2 w-full rounded"
                />
              </div>
              <div>
                <label className="block text-sm font-medium mb-1">{t('adminSuppliers.deliveryStates')}</label>
                <select
                  multiple
                  size={4}
                  value={allowedStates}
                  onChange={(e) => setAllowedStates(Array.from(e.target.selectedOptions, (o) => o.value))}
                  disabled={!offerDelivery}
                  className="border p-2 w-full rounded"
                >
                  {US_STATES.map((code) => (
                    <option key={code} value={code}>{stateLabel(code)}</option>
                  ))}
                </select>
              </div>
            </div>
          </fieldset>

          <fieldset className="border rounded p-3 mt-2">
            <legend className="px-1 text-sm font-medium">{t('adminSuppliers.cuttingLegend')}</legend>
            <div>
              <label className="block text-sm font-medium mb-1">{t('adminSuppliers.cuttingFee')}</label>
              <input
                type="number"
                min="0"
                step="0.01"
                value={cuttingPackagingFee}
                onChange={(e) => setCuttingPackagingFee(e.target.value)}
                className="border p-2 w-full rounded sm:w-1/3"
              />
            </div>
          </fieldset>

          <div className="flex flex-wrap gap-2 mt-2">
            <button
              type="submit"
              className="bg-blue-600 text-white px-4 py-2 rounded"
              disabled={loading}
            >
              {editingSupplier ? t('adminSuppliers.updateSupplier') : t('adminSuppliers.addSupplier')}
            </button>
            {editingSupplier && (
              <button
                type="button"
                onClick={resetForm}
                className="bg-gray-500 text-white px-4 py-2 rounded"
              >
                {t('profile.cancel')}
              </button>
            )}
          </div>
        </form>

        <h3 className="text-lg font-semibold mb-2">{t('adminSuppliers.suppliers')}</h3>
        <ResultCount total={sortedSuppliers.length} page={currentPage} pageSize={SUPPLIERS_PER_PAGE} />
        {isMobile ? (
          loading ? (
            <p className="p-4 text-center">Loading suppliers...</p>
          ) : currentSuppliers.length > 0 ? (
            <ul className="space-y-3">{currentSuppliers.map(renderSupplierCard)}</ul>
          ) : (
            <p className="p-4 text-center text-gray-600">{suppliers.length === 0 ? 'No suppliers available.' : 'No suppliers match the search.'}</p>
          )
        ) : (
        <div className="overflow-x-auto">
          <table className="w-full min-w-[48rem] table-fixed bg-white border rounded shadow">
            <thead>
              <tr className="bg-gray-200">
                <th className="p-3 text-left w-48 cursor-pointer" onClick={() => sortByKey('name')}>{t('adminSuppliers.colName')}{sortArrow('name')}</th>
                <th className="p-3 text-left w-56 cursor-pointer" onClick={() => sortByKey('email')}>{t('adminUsers.email')}{sortArrow('email')}</th>
                <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('phone')}>{t('adminSuppliers.phone')}{sortArrow('phone')}</th>
                <th className="p-3 text-left cursor-pointer" onClick={() => sortByKey('address')}>{t('adminSuppliers.address')}{sortArrow('address')}</th>
                <th className="p-3 text-left">{t('adminSuppliers.photos')}</th>
                <th className="p-3 text-left">{t('adminUsers.actions')}</th>
              </tr>
            </thead>
            <tbody>
              {loading ? (
                <tr>
                  <td colSpan={6} className="border p-4 text-center">
                    Loading suppliers...
                  </td>
                </tr>
              ) : currentSuppliers.length > 0 ? (
                currentSuppliers.map((supplier) => (
                  <tr key={supplier._id} className="border-t hover:bg-gray-50 align-top">
                    <td className="p-3 text-left"><TruncatedText text={supplier.name} /></td>
                    <td className="p-3 text-left"><TruncatedText text={supplier.email || '-'} maxWidthClass="max-w-[16rem]" /></td>
                    <td className="p-3 text-left break-all">{supplier.phone || '-'}</td>
                    <td className="p-3 text-left">
                      {supplier.address?.street}, {supplier.address?.city},{' '}
                      {stateLabel(supplier.address?.state)} {supplier.address?.zip}
                    </td>
                    <td className="p-3 text-left">
                      {renderPhotos(supplier)}
                    </td>
                    <td className="p-3">
                      <div className="flex flex-wrap gap-2">
                      <button
                        onClick={() => openInfoModal(supplier)}
                        className="inline-flex items-center gap-1 bg-indigo-500 text-white px-3 py-1 rounded whitespace-nowrap"
                      >
                        <FaInfoCircle />
                        {t('adminUsers.info')}
                      </button>
                      <button
                        onClick={() => handleEdit(supplier)}
                        className="inline-flex items-center gap-1 bg-yellow-500 text-white px-3 py-1 rounded whitespace-nowrap"
                      >
                        <FaEdit />
                        {t('adminUsers.edit')}
                      </button>
                      {isSuperAdminUser && (
                      <button
                        onClick={() => handleDeleteClick(supplier)}
                        className="inline-flex items-center gap-1 bg-red-600 text-white px-3 py-1 rounded whitespace-nowrap"
                      >
                        <FaTrash />
                        {t('adminUsers.delete')}
                      </button>
                      )}
                      </div>
                    </td>
                  </tr>
                ))
              ) : (
                <tr>
                  <td colSpan={6} className="border p-4 text-center text-gray-600">
                    {suppliers.length === 0
                      ? 'No suppliers available.'
                      : 'No suppliers match the search.'}
                  </td>
                </tr>
              )}
            </tbody>
          </table>
        </div>
        )}

        <Pagination page={currentPage} pageCount={totalPages} onChange={setCurrentPage} />

        <SupplierInfoModal trustedContact
          supplier={selectedSupplier}
          isOpen={modalOpen}
          onClose={() => setModalOpen(false)}
        />

        <ConfirmationModal
          isOpen={showConfirm}
          title={t('adminUsers.deleteTitle')}
          message={t('adminSuppliers.deleteMessage', { name: supplierToDelete?.name })}
          confirmLabel={t('common.confirm')}
          cancelLabel={t('profile.cancel')}
          onConfirm={confirmDelete}
          onClose={cancelDelete}
          username={supplierToDelete?.name}
        />
      </div>
    </div>
  );
}
